import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import {
  CareCuesGroupItem,
  CareCuesInterface,
  CareCuesStateInterface,
  InProgressResponse,
} from './interfaces';
import { selectedVisitSlice } from '../selectedVisit';
import { compactString } from './utilities.ts';

export const careCuesInitialStateValue: CareCuesStateInterface = {
  selectedVisit: null,
  reminders: [],
  loading: false,
  inProgressResponses: [],
  error: null,
};

function setGroupItemLoader(
  state: CareCuesStateInterface,
  group: CareCuesInterface,
  item: CareCuesGroupItem,
  loading: boolean,
) {
  return state.reminders.map((reminder) => {
    if (reminder.id === group.id) {
      return {
        ...reminder,
        items: reminder.items.map((i) => {
          if (i.id === item.id) {
            return {
              ...i,
              loading,
            };
          }

          return i;
        }),
      };
    }

    return reminder;
  });
}

export const careCuesSlice = createSlice({
  name: 'careCues',
  initialState: careCuesInitialStateValue,
  extraReducers: (builder) => {
    builder
      .addCase(selectedVisitSlice.actions.setSelectedVisitDetails, (state, action) => {
        if (!action.payload.uuid || state.selectedVisit === action.payload.uuid) {
          return {
            ...state,
          };
        }

        return {
          ...careCuesInitialStateValue,
          selectedVisit: action.payload.uuid,
        };
      })
      .addCase(selectedVisitSlice.actions.clearVisitDetails, () => {
        return {
          ...careCuesInitialStateValue,
        };
      });
  },
  reducers: {
    loadCareCuesRequested(state, _: PayloadAction<{ noteId: string; isNavigation?: boolean }>) {
      if (state.reminders.length > 0) {
        return { ...state };
      }

      return {
        ...state,
        loading: true,
        error: null,
      };
    },
    loadCareCuesSucceeded(
      state,
      action: PayloadAction<{
        noteId: string;
        reminderGroups: CareCuesInterface[];
        inProgressResponses: InProgressResponse[];
        isNavigation?: boolean;
      }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        loading: false,
        reminders: action.payload.reminderGroups,
        inProgressResponses: action.payload.inProgressResponses,
      };
    },
    loadCareCuesFailed(state, action: PayloadAction<{ noteId: string; error: string }>) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };
    },
    addCareCuesGroupTextResponseRequested(
      state,
      action: PayloadAction<{ noteId: string; response: string; group: CareCuesInterface }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              loading: true,
            };
          }

          return reminder;
        }),
      };
    },
    acceptCreCueItemListRequested(
      state,
      action: PayloadAction<{
        items: CareCuesGroupItem[];
        noteId: string;
        group: CareCuesInterface;
      }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              items: reminder.items.map((item) => ({
                ...item,
                loading: true,
              })),
              loading: true,
            };
          }

          return reminder;
        }),
        error: null,
      };
    },
    acceptCreCueItemListCompleted(
      state,
      action: PayloadAction<{
        noteId: string;
        group: CareCuesInterface;
        successfulItems: CareCuesGroupItem[];
        failedItems: CareCuesGroupItem[];
      }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              items: reminder.items
                // First remove the items that were successfully accepted
                .filter((i) => !action.payload.successfulItems.find((s) => s.id === i.id))
                // Then mark the unsuccessful items as not loading
                .map((item) => ({
                  ...item,
                  loading: false,
                })),
              // Mark the group as not loading
              loading: false,
            };
          }

          return reminder;
        }),
      };
    },
    addCareCuesGroupTextResponseSucceeded(
      state,
      action: PayloadAction<{ noteId: string; group: CareCuesInterface }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              loading: false,
            };
          }

          return reminder;
        }),
      };
    },
    addCareCuesGroupTextResponseFailed(
      state,
      action: PayloadAction<{ error: string; noteId: string; group: CareCuesInterface }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              loading: false,
            };
          }

          return reminder;
        }),
        error: action.payload.error,
      };
    },
    dismissCareCueItemRequested(
      state,
      action: PayloadAction<{ item: CareCuesGroupItem; group: CareCuesInterface; noteId: string }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        error: null,
        reminders: setGroupItemLoader(state, action.payload.group, action.payload.item, true),
      };
    },
    dismissCareCueItemSucceeded(
      state,
      action: PayloadAction<{ item: CareCuesGroupItem; group: CareCuesInterface; noteId: string }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      const currentState = current(state);
      const updatedReminders = currentState.reminders.map((reminder) => ({
        ...reminder,
        items: reminder.items.filter((item) => item.id !== action.payload.item.id),
      }));

      return {
        ...state,
        reminders: updatedReminders,
      };
    },
    dismissCareCueItemFailed(
      state,
      action: PayloadAction<{
        error: string;
        noteId: string;
        item: CareCuesGroupItem;
        group: CareCuesInterface;
      }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        error: action.payload.error,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              items: reminder.items.map((item) => {
                if (item.id === action.payload.item.id) {
                  return {
                    ...item,
                    loading: false,
                  };
                }

                return item;
              }),
            };
          }

          return reminder;
        }),
      };
    },
    pendingResponseDismissed(state, action: PayloadAction<{ noteId: string; isTimeout: boolean }>) {
      return {
        ...state,
        inProgressResponses: state.inProgressResponses.filter(
          (response) => response.noteId !== action.payload.noteId,
        ),
      };
    },
    careCuesSeen(
      state,
      _: PayloadAction<{
        noteId: string;
        reminderGroups: CareCuesInterface[];
        isNavigation?: boolean;
      }>,
    ) {
      return {
        ...state,
      };
    },
    careCueItemTextUpdateRequested(
      state,
      action: PayloadAction<{ noteId: string; item: CareCuesGroupItem; group: CareCuesInterface }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: setGroupItemLoader(state, action.payload.group, action.payload.item, true),
        error: null,
      };
    },
    careCueItemTextUpdateSucceeded(
      state,
      action: PayloadAction<{ noteId: string; item: CareCuesGroupItem; group: CareCuesInterface }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        loading: false,
        reminders: state.reminders.map((reminder) => {
          if (reminder.id === action.payload.group.id) {
            return {
              ...reminder,
              items: reminder.items.map((item) => {
                if (item.id === action.payload.item.id) {
                  return {
                    ...item,
                    loading: false,
                    displayText: action.payload.item.displayText,
                    displayTextHash: compactString(action.payload.item.displayText),
                  };
                }
                return item;
              }),
            };
          }
          return reminder;
        }),
      };
    },
    careCueItemTextUpdateFailed(
      state,
      action: PayloadAction<{
        noteId: string;
        error: string;
        item: CareCuesGroupItem;
        group: CareCuesInterface;
      }>,
    ) {
      if (state.selectedVisit !== action.payload.noteId) {
        return { ...state };
      }

      return {
        ...state,
        reminders: setGroupItemLoader(state, action.payload.group, action.payload.item, false),
        error: action.payload.error,
      };
    },
  },
});

export const {
  loadCareCuesRequested,
  loadCareCuesSucceeded,
  loadCareCuesFailed,
  addCareCuesGroupTextResponseRequested,
  addCareCuesGroupTextResponseSucceeded,
  addCareCuesGroupTextResponseFailed,
  dismissCareCueItemRequested,
  dismissCareCueItemSucceeded,
  dismissCareCueItemFailed,
  pendingResponseDismissed,
  acceptCreCueItemListRequested,
  acceptCreCueItemListCompleted,
  careCueItemTextUpdateRequested,
  careCueItemTextUpdateSucceeded,
  careCueItemTextUpdateFailed,
} = careCuesSlice.actions;

export default careCuesSlice.reducer;
